import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getBotSettings, updateBotSettings } from '@/lib/api/botSettings'
import { useAuthStore } from '@/store/authStore'

export const MAX_CUSTOM_RULES_CHARS = 4000

const EXAMPLES = [
  'No atendemos pedidos de más de 10 unidades por WhatsApp: pide al cliente que llame a la tienda.',
  'Si preguntan por instalación, aclara que la instalación la hace un taller aliado y no la tienda.',
  'Los domingos solo hay retiro en tienda hasta la 1:00 pm.',
  'Trata al cliente de "usted".',
]

const customRulesKey = (tenantId: string | null) => ['bot-settings', tenantId, 'custom-rules'] as const

export interface CustomRulesEditorViewProps {
  value: string
  isDirty: boolean
  isSaving: boolean
  saveError: string | null
  onChange: (value: string) => void
  onSave: () => void
}

/** Editor de reglas propias del negocio. Solo presentación. */
export function CustomRulesEditorView({ value, isDirty, isSaving, saveError, onChange, onSave }: CustomRulesEditorViewProps) {
  const remaining = MAX_CUSTOM_RULES_CHARS - value.length
  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
        <p className="flex items-center gap-2 font-semibold text-slate-900">
          <ShieldCheck className="h-4 w-4 text-[#075E54]" aria-hidden="true" />
          Opción avanzada
        </p>
        <p className="mt-1">
          Escribe, una por línea, las reglas de tu negocio que el asistente debe seguir y que no están en otras pantallas. El asistente
          las aplica en todas las conversaciones, pero nunca por encima de las reglas de seguridad: no inventará precios, inventario,
          envíos ni datos de pago, ni confirmará pedidos o citas por su cuenta.
        </p>
      </div>

      <div>
        <label htmlFor="custom-agent-rules" className="block text-sm font-semibold text-slate-800">
          Reglas propias de tu negocio
        </label>
        <textarea
          id="custom-agent-rules"
          value={value}
          maxLength={MAX_CUSTOM_RULES_CHARS}
          rows={12}
          disabled={isSaving}
          onChange={(event) => onChange(event.target.value)}
          placeholder={EXAMPLES.map((example) => `- ${example}`).join('\n')}
          aria-describedby="custom-agent-rules-count"
          className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#075E54]/30"
        />
        <p id="custom-agent-rules-count" className={`mt-1 text-xs ${remaining < 200 ? 'text-amber-700' : 'text-slate-500'}`}>
          {remaining} caracteres disponibles
        </p>
      </div>

      {saveError && (
        <p role="alert" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{saveError}</span>
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <p className="text-xs text-slate-500">{isDirty ? 'Tienes cambios sin guardar.' : 'Todo guardado.'}</p>
        <Button type="button" disabled={!isDirty || isSaving} onClick={onSave} className="bg-[#075E54] hover:bg-[#064c44]">
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {isSaving ? 'Guardando...' : 'Guardar reglas'}
        </Button>
      </div>
    </div>
  )
}

function CustomRulesEditor({ saved, tenantId }: { saved: string; tenantId: string | null }) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState(saved)
  const mutation = useMutation({
    mutationFn: () => updateBotSettings({ customAgentRules: value.trim() ? value : null }),
    onSuccess: () => {
      toast.success('Reglas guardadas')
      void queryClient.invalidateQueries({ queryKey: customRulesKey(tenantId) })
    },
  })
  const saveError = mutation.isError ? 'No se pudieron guardar las reglas. Inténtalo de nuevo.' : null

  return (
    <CustomRulesEditorView
      value={value}
      isDirty={value.trim() !== saved.trim()}
      isSaving={mutation.isPending}
      saveError={saveError}
      onChange={(next) => {
        mutation.reset()
        setValue(next)
      }}
      onSave={() => mutation.mutate()}
    />
  )
}

export default function CustomRulesForm() {
  const tenantId = useAuthStore((state) => state.tenantId)
  const query = useQuery({
    queryKey: customRulesKey(tenantId),
    queryFn: async () => (await getBotSettings()).customAgentRules ?? '',
  })

  if (query.isPending) {
    return (
      <div className="space-y-3 p-4 sm:p-6" aria-busy="true" aria-label="Cargando reglas">
        <Skeleton height={72} className="w-full" />
        <Skeleton height={220} className="w-full" />
      </div>
    )
  }
  if (query.isError) {
    return (
      <div role="alert" className="m-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 sm:m-6">
        <p className="flex items-center gap-2 text-sm font-medium text-rose-800">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          No pudimos cargar las reglas.
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
          Reintentar
        </Button>
      </div>
    )
  }
  return <CustomRulesEditor key={query.dataUpdatedAt} saved={query.data ?? ''} tenantId={tenantId} />
}
